/**
 * Guest Upload Events Page
 *
 * Allows hosts to create and manage guest upload events.
 * Guests can upload videos without an account via QR code or link.
 */

import { useEffect, useState } from "react";
import { useSnapshot } from "valtio";
import { toast } from "sonner";
import {
  eventsStore,
  fetchEvents,
  deactivateEvent,
  deleteEvent,
  fetchEventUploads,
  selectEvent,
  getQRCodeUrl,
  getUploadUrl,
  getTimeRemaining,
  type GuestEvent,
} from "./stores/events";
import { CreateEventModal } from "./CreateEventModal";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  AlertCircle,
  Plus,
  QrCode,
  Copy,
  Users,
  Clock,
  Upload,
  Trash2,
  StopCircle,
  ExternalLink,
  Download,
} from "lucide-react";

// =============================================================================
// QR Code Dialog
// =============================================================================

function QRCodeDialog({
  open,
  onOpenChange,
  event,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: GuestEvent | null;
}) {
  if (!event) return null;

  const uploadUrl = getUploadUrl(event.code);
  const qrUrl = getQRCodeUrl(event.code, 400);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(uploadUrl);
    toast.success("Upload link copied to clipboard");
  };

  const handleDownloadQR = () => {
    const link = document.createElement("a");
    link.href = qrUrl;
    link.download = `${event.name.replace(/\s+/g, "_")}_QR.png`;
    link.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share Upload Link</DialogTitle>
          <DialogDescription>
            Guests can scan this QR code or use the link to upload videos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* QR Code */}
          <div className="flex justify-center p-4 bg-white rounded-lg">
            <img
              src={qrUrl}
              alt={`QR Code for ${event.name}`}
              className="w-64 h-64"
            />
          </div>

          {/* Event Info */}
          <div className="text-center space-y-1">
            <p className="font-semibold text-lg">{event.name}</p>
            <p className="text-3xl font-mono font-bold tracking-wider">
              {event.code}
            </p>
          </div>

          {/* Upload URL */}
          <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
            <code className="flex-1 text-sm truncate">{uploadUrl}</code>
            <Button variant="ghost" size="icon" onClick={handleCopyLink}>
              <Copy className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => window.open(uploadUrl, "_blank")}
            >
              <ExternalLink className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadQR}>
            <Download className="h-4 w-4 mr-2" />
            Download QR
          </Button>
          <Button onClick={handleCopyLink}>
            <Copy className="h-4 w-4 mr-2" />
            Copy Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// Event Details Sheet
// =============================================================================

function EventDetailsSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { selectedEvent, selectedEventUploads, uploadsLoading } =
    useSnapshot(eventsStore);

  useEffect(() => {
    if (selectedEvent && open) {
      fetchEventUploads(selectedEvent.code);
    }
  }, [selectedEvent, open]);

  if (!selectedEvent) return null;

  const totalSize = selectedEventUploads.reduce((sum, u) => sum + u.size, 0);
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{selectedEvent.name}</SheetTitle>
          <SheetDescription>
            Code:{" "}
            <span className="font-mono font-bold">{selectedEvent.code}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">
                  {selectedEvent.uploadCount}
                </div>
                <p className="text-sm text-muted-foreground">
                  of {selectedEvent.maxUploads} uploads
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">
                  {selectedEvent.contributorCount}
                </div>
                <p className="text-sm text-muted-foreground">contributors</p>
              </CardContent>
            </Card>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <h4 className="font-medium">Status</h4>
            <div className="flex items-center gap-2">
              <Badge variant={selectedEvent.isActive ? "default" : "secondary"}>
                {selectedEvent.isActive ? "Active" : "Inactive"}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {getTimeRemaining(selectedEvent.expiresAt)}
              </span>
            </div>
          </div>

          {/* Uploads */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-medium">Uploads</h4>
              {selectedEventUploads.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {formatSize(totalSize)} total
                </span>
              )}
            </div>

            {uploadsLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : selectedEventUploads.length > 0 ? (
              <div className="space-y-2">
                {selectedEventUploads.map((upload, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {upload.key.split("/").pop()}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {upload.contributorName || "Anonymous"}
                        {upload.contributorRelation &&
                          ` - ${upload.contributorRelation}`}
                      </p>
                    </div>
                    <span className="text-sm text-muted-foreground ml-2">
                      {formatSize(upload.size)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No uploads yet
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export default function GuestUploadPage() {
  const { events, loading, error } = useSnapshot(eventsStore);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrEvent, setQrEvent] = useState<GuestEvent | null>(null);
  const [detailsSheetOpen, setDetailsSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<GuestEvent | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [eventToDeactivate, setEventToDeactivate] = useState<GuestEvent | null>(
    null,
  );
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleShowQR = (event: GuestEvent) => {
    setQrEvent(event);
    setQrDialogOpen(true);
  };

  const handleShowDetails = (event: GuestEvent) => {
    selectEvent(event);
    setDetailsSheetOpen(true);
  };

  const handleDeactivate = async () => {
    if (!eventToDeactivate) return;
    setDeactivateLoading(true);
    try {
      const success = await deactivateEvent(eventToDeactivate.id);
      if (success) {
        toast.success("Event deactivated");
      } else {
        toast.error("Failed to deactivate event");
      }
      setDeactivateDialogOpen(false);
      setEventToDeactivate(null);
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!eventToDelete) return;
    setDeleteLoading(true);
    try {
      const success = await deleteEvent(eventToDelete.id);
      if (success) {
        toast.success("Event deleted");
      } else {
        toast.error("Failed to delete event");
      }
      setDeleteDialogOpen(false);
      setEventToDelete(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Separate active and inactive events
  const activeEvents = events.filter(
    (e) => e.isActive && Date.now() < e.expiresAt,
  );
  const inactiveEvents = events.filter(
    (e) => !e.isActive || Date.now() >= e.expiresAt,
  );

  return (
    <div className="flex h-full flex-col">
      {/* Fixed Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Guest Uploads</h1>
            <p className="mt-2 text-muted-foreground">
              Create events for guests to upload videos without an account
            </p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Event
          </Button>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 space-y-6">
          {/* Error State */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* How It Works */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <p className="font-medium">Create Event</p>
                    <p className="text-sm text-muted-foreground">
                      Set a name and upload limits
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-medium">Share QR Code</p>
                    <p className="text-sm text-muted-foreground">
                      Guests scan to upload videos
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-medium">Collect Videos</p>
                    <p className="text-sm text-muted-foreground">
                      Videos auto-delete after 7 days
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Active Events */}
          <Card>
            <CardHeader>
              <CardTitle>Active Events ({activeEvents.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16" />
                  ))}
                </div>
              ) : activeEvents.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Uploads</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeEvents.map((event) => (
                      <TableRow key={event.id}>
                        <TableCell>
                          <button
                            className="font-medium hover:underline text-left"
                            onClick={() => handleShowDetails(event)}
                          >
                            {event.name}
                          </button>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {event.contributorCount} contributors
                          </div>
                        </TableCell>
                        <TableCell>
                          <code className="text-lg font-mono font-bold">
                            {event.code}
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Upload className="h-4 w-4 text-muted-foreground" />
                            {event.uploadCount} / {event.maxUploads}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            {getTimeRemaining(event.expiresAt)}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleShowQR(event)}
                              title="Show QR Code"
                            >
                              <QrCode className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEventToDeactivate(event);
                                setDeactivateDialogOpen(true);
                              }}
                              title="Stop Accepting Uploads"
                            >
                              <StopCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              onClick={() => {
                                setEventToDelete(event);
                                setDeleteDialogOpen(true);
                              }}
                              title="Delete Event"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Upload className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No active events</p>
                  <p className="text-sm">
                    Create an event to start collecting videos
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Inactive Events */}
          {inactiveEvents.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-muted-foreground">
                  Past Events ({inactiveEvents.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Uploads</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inactiveEvents.map((event) => (
                      <TableRow key={event.id} className="opacity-60">
                        <TableCell>
                          <button
                            className="font-medium hover:underline text-left"
                            onClick={() => handleShowDetails(event)}
                          >
                            {event.name}
                          </button>
                        </TableCell>
                        <TableCell>
                          <code className="font-mono">{event.code}</code>
                        </TableCell>
                        <TableCell>{event.uploadCount} uploads</TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {Date.now() >= event.expiresAt
                              ? "Expired"
                              : "Deactivated"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => {
                              setEventToDelete(event);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* Create Event Modal */}
      <CreateEventModal
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* QR Code Dialog */}
      <QRCodeDialog
        open={qrDialogOpen}
        onOpenChange={setQrDialogOpen}
        event={qrEvent}
      />

      {/* Event Details Sheet */}
      <EventDetailsSheet
        open={detailsSheetOpen}
        onOpenChange={(open) => {
          setDetailsSheetOpen(open);
          if (!open) selectEvent(null);
        }}
      />

      {/* Deactivate Confirmation */}
      <ConfirmDialog
        open={deactivateDialogOpen}
        onOpenChange={setDeactivateDialogOpen}
        title="Stop Accepting Uploads"
        description={`Are you sure you want to deactivate "${eventToDeactivate?.name}"? Guests will no longer be able to upload videos.`}
        confirmLabel="Deactivate"
        variant="destructive"
        onConfirm={handleDeactivate}
        loading={deactivateLoading}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Event"
        description={`Are you sure you want to delete "${eventToDelete?.name}"? This will permanently delete all ${eventToDelete?.uploadCount || 0} uploaded videos.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        loading={deleteLoading}
      />
    </div>
  );
}
